/**
 * Supabase client singleton
 *
 * This module provides a typed Supabase client configured for the application.
 * Use this client for all database operations.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { guardSupabaseFetch } from '@/lib/supabaseRequestGuard'
import type { Database } from '@/types/supabase'

let supabaseClient: SupabaseClient<Database> | null = null

function createSupabaseClient(): SupabaseClient<Database> {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
  const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error(
      'Missing Supabase environment variables. Set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY',
    )
  }

  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true, // Required for OAuth redirects
    },
    // Reports reads that near the URL limit or come back trimmed to the row cap.
    global: { fetch: guardSupabaseFetch() },
  })
}

export function getSupabaseClient(): SupabaseClient<Database> {
  if (!supabaseClient) {
    supabaseClient = createSupabaseClient()
  }

  return supabaseClient
}

export const supabase = new Proxy({} as SupabaseClient<Database>, {
  get(_target, prop) {
    const client = getSupabaseClient()
    const value = client[prop as keyof SupabaseClient<Database>]

    if (typeof value === 'function') {
      return value.bind(client)
    }

    return value
  },
})
